import { z } from 'zod';

import {
  kgInsightCategorySchema,
  kgInsightSubcategorySchema,
} from './kg-insights';
import { BENZINGA, MTN } from './news';

const inventoryEntityItem = z.object({
  tag: z.string(),
});

const inventoryTimeseriesItem = z.object({
  snake: z.string(),
});

const inventoryWebLinkItem = z.object({
  urls: z.array(z.string()).nullish(),
});

const inventoryExternalDataItem = z.object({
  links: z.array(z.string()).nullish(),
});

const inventoryArtifactItem = z
  .object({
    artifact_id: z.string(),
    artifactType: z.string().nullish(),
    artifact_type: z.string().nullish(),
    hidden: z.boolean().nullish(),
    is_hidden: z.boolean().nullish(),
  })
  .passthrough();

const sourceSchema = z.union([z.literal(BENZINGA), z.literal(MTN)]);

const inventoryNewsItem = z.object({
  article_id: z.string(),
  title: z.string(),
  source: sourceSchema,
  published: z.string(),
});

const inventoryWatchlistItem = z.object({
  id: z.string(),
  name: z.string(),
});

const inventoryInsightItem = z.object({
  id: z.string(),
});

const isoDateString = z
  .string()
  .refine(val => !isNaN(new Date(val).getTime()), {
    message: 'Invalid date string',
  });

const inventoryEconomicCalendarItem = z.object({
  categories: z.array(z.string()).nullish(),
  countries: z.array(z.string()).nullish(),
  from: isoDateString,
  to: isoDateString,
  entity_tag: z.string().optional(),
});

export type InventoryEconomicCalendarItem = z.infer<
  typeof inventoryEconomicCalendarItem
>;

const financialStatementType = z.enum(['income', 'cashflow', 'balancesheet']);

const financialStatementsItem = z.object({
  tag: z.string(),
  statement: financialStatementType.optional(),
});

export type FinancialStatementInventoryType = z.infer<
  typeof financialStatementType
>;

const documentSchema = z.object({
  Authority: z.number(),
  Country: z.string(),
  Entity: z.string(),
  ID: z.string(),
  Kind: z.string(),
  Rank: z.number(),
});

export type DocumentSearchSchema = z.infer<typeof documentSchema>;

const documentSearchItem = z.object({
  question: z.string().optional(),
  citation_groups: z.array(z.string()).nullish(),
  documents: z.array(documentSchema).nullish(),
});

export type DocumentSearchItem = z.infer<typeof documentSearchItem>;
export type InventoryWatchlistItem = z.infer<typeof inventoryWatchlistItem>;

export type InventoryNewsItem = z.infer<typeof sourceSchema>;

/** Anchor type for KG inventory rows. */
export const inventoryKgNodeType = z.enum(['company', 'theme']);

export type InventoryKgNodeType = z.infer<typeof inventoryKgNodeType>;

/** Atomic relation (paired with `node_type` + `node_key`). */
export const inventoryKgRelationType = z.enum([
  'theme',
  'company',
  'product',
  'country',
  'region',
  'competitor',
]);

export type InventoryKgRelationType = z.infer<typeof inventoryKgRelationType>;

export const inventoryKgFilterItemSchema = z.object({
  node_type: inventoryKgNodeType,
  node_key: z.string(),
});

export type InventoryKgFilterItem = z.infer<typeof inventoryKgFilterItemSchema>;

export const inventoryKgItemSchema = z.object({
  node_type: inventoryKgNodeType,
  node_key: z.string(),
  relation_type: inventoryKgRelationType,
  filter: z.array(inventoryKgFilterItemSchema).optional(),
});

export type InventoryKgItem = z.infer<typeof inventoryKgItemSchema>;

export const conversationInventoryKnowledgeGraphKey =
  'knowledge-graph' as const;

export const conversationInventoryKgInsightKey = 'kg-insight' as const;

export const inventoryKgInsightItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal(conversationInventoryKgInsightKey),
  category: kgInsightCategorySchema,
  sub_category: kgInsightSubcategorySchema.nullish(),
});

export type InventoryKgInsightItem = z.infer<
  typeof inventoryKgInsightItemSchema
>;

export const isKgInsightItem = <T extends object>(
  item: T
): item is T & InventoryKgInsightItem =>
  'type' in item && item.type === conversationInventoryKgInsightKey;

export const microsoftServiceKeys = [
  'microsoft_onedrive',
  'microsoft_outlook',
  'microsoft_onenote',
  'microsoft_teams',
] as const;
export type MicrosoftServiceKeysType = (typeof microsoftServiceKeys)[number];

const microsoftServiceItem = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  size: z.number().nullish(),
});
export type MicrosoftServiceItem = z.infer<typeof microsoftServiceItem>;

const customServiceToolItem = z.object({
  id: z.string(),
  connector: z.string(),
  name: z.string(),
  summary: z.string(),
  status: z.string(),
  body: z.string(),
  body_truncated: z.boolean().optional(),
});

export type CustomServiceToolItem = z.infer<typeof customServiceToolItem>;

export type CustomServiceInventoryItem = {
  name: string;
  items: CustomServiceToolItem[];
};

const inventoryInsightItemWithTitle = z.object({
  id: z.string(),
  title: z.string(),
});

export type InventoryInsightItemWithTitle = z.infer<
  typeof inventoryInsightItemWithTitle
>;

const inventoryScreenAnalysisItem = z.object({
  description: z.string(),
  filters: z.string(),
  matches: z.number(),
  tool: z.string().optional(),
});

export type InventoryScreenAnalysisItem = z.infer<
  typeof inventoryScreenAnalysisItem
>;

const inventoryScenarioAnalysisItem = z.object({
  description: z.string(),
  benchmark: z.string().optional(),
  entities: z.number(),
  event_mode: z.string().optional(),
  n_events: z.number().optional(),
  tool: z.string().optional(),
  entity: z.string().optional(),
});

export type InventoryScenarioAnalysisItem = z.infer<
  typeof inventoryScenarioAnalysisItem
>;

// `analysis` items come from different backend tools (screen_entities,
// run_scenario, ...) with non-overlapping shapes, so a plain union (tried in
// order, no shared discriminant) is more resilient than a discriminated
// union: `tool` is only optional here for backwards compatibility with older
// conversations that predate it.
const inventoryAnalysisItem = z.union([
  inventoryScreenAnalysisItem,
  inventoryScenarioAnalysisItem,
]);

export type InventoryAnalysisItem = z.infer<typeof inventoryAnalysisItem>;

// Discriminate on tool; fall back to field presence for rows predating the tool field.
export const isScreenAnalysisItem = (
  item: InventoryAnalysisItem
): item is InventoryScreenAnalysisItem =>
  item.tool === 'screen_entities' ||
  (item.tool === undefined && 'filters' in item);

export const isScenarioAnalysisItem = (
  item: InventoryAnalysisItem
): item is InventoryScenarioAnalysisItem =>
  item.tool === 'run_scenario' ||
  (item.tool === undefined && 'benchmark' in item);

const conversationInventorySchema = z.object({
  entity: z.object({ items: z.array(inventoryEntityItem) }).optional(),
  timeseries: z.object({ items: z.array(inventoryTimeseriesItem) }).optional(),
  artifact: z.object({ items: z.array(inventoryArtifactItem) }).optional(),
  insight: z.object({ items: z.array(inventoryInsightItem) }).optional(),
  news: z.object({ items: z.array(inventoryNewsItem) }).optional(),
  watchlist: z.object({ items: z.array(inventoryWatchlistItem) }).optional(),
  ['web-links']: z.object({ items: z.array(inventoryWebLinkItem) }).optional(),
  ['external-data']: z
    .object({ items: z.array(inventoryExternalDataItem) })
    .optional(),
  ['financial-statement']: z
    .object({ items: z.array(financialStatementsItem) })
    .optional(),
  ['document-search']: z
    .object({ items: z.array(documentSearchItem) })
    .optional(),
  [conversationInventoryKnowledgeGraphKey]: z
    .object({ items: z.array(inventoryKgItemSchema) })
    .optional(),
  ['economic-calendar']: z
    .object({ items: z.array(inventoryEconomicCalendarItem) })
    .optional(),
  ['earnings-insight']: z
    .object({ items: z.array(inventoryInsightItemWithTitle) })
    .optional(),
  ['catalyst-insight']: z
    .object({ items: z.array(inventoryInsightItemWithTitle) })
    .optional(),
  [conversationInventoryKgInsightKey]: z
    .object({ items: z.array(inventoryKgInsightItemSchema) })
    .optional(),
  ['microsoft_onedrive']: z
    .object({ items: z.array(microsoftServiceItem) })
    .optional(),
  ['microsoft_outlook']: z
    .object({ items: z.array(microsoftServiceItem) })
    .optional(),
  ['microsoft_onenote']: z
    .object({ items: z.array(microsoftServiceItem) })
    .optional(),
  ['microsoft_teams']: z
    .object({ items: z.array(microsoftServiceItem) })
    .optional(),
  custom_service: z
    .object({ items: z.array(customServiceToolItem) })
    .optional(),
  analysis: z.object({ items: z.array(inventoryAnalysisItem) }).optional(),
});

export const inventory = {
  conversationInventory: {
    path: (conversationId: string) =>
      `conversation-inventory/v1/conversations/${conversationId}`,
    schema: conversationInventorySchema,
  },
};
